import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  isPossiblePhoneNumber,
  AsYouType,
} from "libphonenumber-js";
import { getFlagAndCodeForCountry } from "./countryUtils";

export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
  errorType?: string;
}

export function validatePhoneNumber(
  phoneNumberString: string
): PhoneValidationResult {
  if (!phoneNumberString || phoneNumberString.trim() === "") {
    return { isValid: true }; // Empty is valid (unless required)
  }

  try {
    const phoneNumber = parsePhoneNumberFromString(phoneNumberString);
    const validationError = validatePhoneNumberLength(phoneNumberString);

    if (validationError) {
      const errorMessages: Record<string, string> = {
        TOO_SHORT: "Phone number is too short",
        TOO_LONG: "Phone number is too long",
        INVALID_COUNTRY: "Invalid country code",
        NOT_A_NUMBER: "Enter a valid phone number",
      };
      return {
        isValid: false,
        error: errorMessages[validationError] || "Enter a valid phone number",
        errorType: validationError,
      };
    }

    const isValid = phoneNumber?.isValid() ?? false;
    if (!isValid) {
      // Additional check for possible phone numbers
      const isPossible = isPossiblePhoneNumber(phoneNumberString);
      if (!isPossible) {
        return {
          isValid: false,
          error: "Enter a valid phone number",
          errorType: "NOT_A_NUMBER",
        };
      }
    }

    return {
      isValid,
      error: isValid ? undefined : "Enter a valid phone number",
    };
  } catch (error) {
    return {
      isValid: false,
      error: "Enter a valid phone number",
      errorType: "INVALID_FORMAT",
    };
  }
}

export function convertPhoneNumberToE164(
  phoneNumber: string,
  defaultCountry: string = "US"
): string {
  try {
    const parsed = parsePhoneNumberFromString(
      phoneNumber,
      defaultCountry as any
    );
    return parsed?.format("E.164") ?? phoneNumber;
  } catch (error) {
    return phoneNumber;
  }
}

export function formatPhoneNumberForDisplay(
  phoneNumber: string,
  defaultCountry: string = "US"
): string {
  try {
    const parsed = parsePhoneNumberFromString(
      phoneNumber,
      defaultCountry as any
    );
    return parsed?.formatNational() ?? phoneNumber;
  } catch (error) {
    return phoneNumber;
  }
}

export function formatPhoneNumber(
  phoneNumber: string,
  countryName: string
): string {
  if (!phoneNumber) return "";

  const countryCode = getFlagAndCodeForCountry(countryName).code;
  const asYouType = new AsYouType(countryName as any);

  // Clean input to digits only
  const digits = phoneNumber.replace(/[^\d]/g, "");

  // Format using AsYouType
  const fullNumber = `+${countryCode}${digits}`;
  const formatted = asYouType.input(fullNumber);

  // Return only the national part (remove country code prefix)
  const prefix = `+${countryCode}`;
  return formatted.startsWith(prefix)
    ? formatted.substring(prefix.length).trim()
    : formatted;
}
